import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper';

export interface Fine {
  id: number;
  member_id: number;
  group_id: number;
  reason: string;
  amount: number;
  issued_by: number;
  status: 'unpaid' | 'paid' | 'waived';
  created_at: string;
  paid_at: string | null;
  user?: { id: number; name: string };
  issuer?: { id: number; name: string };
  group?: { id: number; name: string };
}
export interface FinePaymentPayload {
  fine_id: number;
  amount: number;
  payment_method: 'bank_transfer' | 'cash' | 'mobile_money' | 'digital_wallet' | 'cheque';
  payment_date: string;
  description?: string;
  transaction_reference?: string;
}

export const useFines = (options: { search?: string } = {}) => {
  const { search } = options;
  
  return useQuery({
    queryKey: ['fines', { search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const { data } = await api.get(`/fines?${params.toString()}`);
      return data;
    },
  });
};
export const usePayFine = (options: { id?: number } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ( payload: FinePaymentPayload) => {
      const { data } = await api.post(`/fines/payFine`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fines'] });
      toast.success('Fine payment made successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};


// Hook for fetching unpaid fines
export const useGetUnpaidFines = (filters: {
  page?: number;
  per_page?: number;
  member_id?: number;
  } = {}) => {
    filters = { ...filters , ...{status: 'unpaid'}}
    return useQuery({
      queryKey: ['unpaid-fines', filters],
      queryFn: async () => {
        const { data } = await api.get('/fines', { 
          params: filters 
        });
        return data.data;
      },
    });
};

export const useCreateFine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fine: Omit<Fine, 'id' | 'group_id' | 'issued_by' | 'created_at' | 'member' | 'issuer' | 'group'>) => {
      const { data } = await api.post('/fines', fine);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fines'] });
      toast.success('Fine created successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useUpdateFine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...fine }: Fine) => {
      const { data } = await api.put(`/fines/${id}`, fine);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fines'] });
      toast.success('Fine updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useDeleteFine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/fines/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fines'] });
      toast.success('Fine deleted successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

// Hook to export fines
export const useExportFines = () => {
  return useMutation({
    mutationFn: async (filters: { search?: string, format?: 'excel' | 'pdf' | 'csv' } = {}) => {
      const params = {
        type: filters.format || 'excel',
        ...Object.fromEntries(
          Object.entries(filters).filter(([key, value]) => 
            value !== undefined && value !== null && value !== '' && key !== 'format'
          )
        )
      };
      
      const response = await api.get('/fines/export', {
        params: params,
        responseType: 'blob',
      });
      
      // Create a download link with whatever the backend returns
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Use the filename from the backend response headers if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'fines';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // If no filename from headers, determine from type parameter
      if (filename === 'fines') {
        const type = filters.format || 'excel';
        const extensions = {
          'excel': 'xlsx',
          'csv': 'csv', 
          'pdf': 'pdf'
        };
        filename = `fines.${extensions[type] || 'xlsx'}`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
}; 